import { publish } from 'pub-sub';

import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, v, w } from '@dojo/framework/core/vdom';
import Button from '@dojo/widgets/button';
import {
	createNlsMiddleware,
	keymap,
	lccstate,
	pathmap,
	trackingWYLOChange,
	userstate,
	documentapi
} from '@holmescorp/lcc-state';
import { Keys } from '@holmescorp/common';

import css from '../css/where-you-left-off.m.css';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// eslint-disable-next-line @typescript-eslint/no-unused-vars

interface WhereYouLeftOffIcache {
	always_resume: boolean;
}

export interface WhereYouLeftOffProperties {
	onAlwaysResume?: (resumeFunction: () => void) => void;
}

const nlsSettings = createNlsMiddleware('settings');
const icache = createICacheMiddleware<WhereYouLeftOffIcache>();
const factory = create({
	icache,
	theme,
	nlsSettings,
	lccstate,
	userstate,
	documentapi
}).properties<WhereYouLeftOffProperties>();

export const WhereYouLeftOff = factory(
	({ properties, middleware: { icache, theme, nlsSettings, userstate, documentapi } }) => {
		const { onAlwaysResume } = properties();
		const themeCss = theme.classes(css);

		const {
			messages: {
				wylo_title = '[settings] wylo_title',
				wylo_header = '[settings] wylo_header',
				wylo_checkbox = '[settings] wylo_checkbox',
				wylo_yes = '[settings] wylo_yes',
				wylo_no = '[settings] wylo_no'
			}
		} = nlsSettings();

		documentapi.setTitle(wylo_header);

		const { get, path } = userstate;
		const tracking = get(path('toolstate', 'trackingwylo'));
		const status = get(path('status'));

		const ttopic = tracking.tracking.topic;
		const ttopicgroup = tracking.tracking.topicgroup;
		const ttopicset = tracking.tracking.topicset;
		const ttopichead = tracking.tracking.topichead;

		const always_resume = icache.getOrSet('always_resume', false);
		const alwaysResume = tracking.always_resume;

		const confirm = () => {
			if (ttopichead === 'practice') {
				publish('site/practice');
			} else if (ttopicgroup === 'quiz' || ttopicgroup === 'flashcards') {
				const { path } = keymap.get(ttopicset)!;
				const { view } = pathmap.get(`${path}/${ttopicgroup}`)!;
				view();
			} else {
				const topic = status[ttopicgroup];
				const topicStatus = topic.data;
				const nextTopic = String(topic.next);
				const readingsComplete = topicStatus.complete === topicStatus.total;

				if (readingsComplete && nextTopic) {
					const { path } = keymap.get(nextTopic)!;
					const { view } = pathmap.get(path)!;
					view();
				} else {
					const { path } = keymap.get(ttopicgroup)!;
					const { view } = ttopic ? pathmap.get(`${path}/${ttopic}`)! : pathmap.get(path)!;
					view();
				}
			}
		};

		const decline = () => {
			publish('mystudyplan/view');
		};

		if (onAlwaysResume) {
			onAlwaysResume(() => confirm());
		}

		const handleCheckboxChange = (checked: boolean) => {
			icache.set('always_resume', !checked);
			trackingWYLOChange({ always_resume: !checked });
		};

		return alwaysResume && !always_resume
			? v('div', {}, []) // Display quick blank while loading auto resume
			: v('div', { key: 'where-you-left-off', xqa: 'where-you-left-off', classes: [themeCss.root] }, [
					v('h1', { classes: [themeCss.title] }, [wylo_title]),
					v('fieldset', {}, [
						v('legend', { classes: [themeCss.header] }, [wylo_header]),
						v('div', { classes: [themeCss.checkBox] }, [
							v('input', {
								type: 'checkbox',
								id: 'wylo-checkbox',
								checked: always_resume,
								onclick: (event: MouseEvent) => {
									event.stopPropagation();
									handleCheckboxChange(always_resume);
								},
								onkeydown: (event) => {
									switch (event.key) {
										case Keys.Enter:
										case Keys.Space:
										case Keys.Spacebar:
											event.preventDefault();
											handleCheckboxChange(always_resume);
											break;
									}
								}
							}),
							v('label', { for: 'wylo-checkbox' }, [wylo_checkbox])
						]),
						w(Button, { onClick: confirm }, [wylo_yes]),
						w(Button, { onClick: decline }, [wylo_no])
					])
			  ]);
	}
);
